import { Alert, Box, Chip, Grid, Stack, Typography } from '@mui/material';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PlaceIcon from '@mui/icons-material/Place';
import TicketCard from './TicketCard';
import RegistrationSummary from './RegistrationSummary';
import { registrationStyles } from './eventDetailsStyles';
import { formatCurrency, formatDate, formatTime, isTicketAvailable } from './eventDetailsUtils';

const SessionCard = ({
  session,
  isSelected,
  isAvailable,
  shouldShowRemainingTickets = false,
  sessionAvailabilityLabel,
  sessionTicketTypes,
  selectedTicketCode,
  selectedTicket,
  selectedTicketSeatsLeft,
  quantity,
  totalPrice,
  formErrors,
  onSessionSelect,
  onTicketSelect,
  onQuantityChange,
  getTicketQuantityLimit,
  onOpenRegistration,
  summaryDisabled,
}) => {
  const selectableTicketCount = sessionTicketTypes.filter(isTicketAvailable).length;

  return (
    <Box
      role="button"
      tabIndex={isAvailable ? 0 : -1}
      aria-selected={isSelected}
      aria-disabled={!isAvailable}
      onClick={() => onSessionSelect(session)}
      onKeyDown={(event) => {
        if (!isAvailable || (event.key !== 'Enter' && event.key !== ' ')) return;
        event.preventDefault();
        onSessionSelect(session);
      }}
      sx={registrationStyles.sessionCard(isSelected, isAvailable)}
    >
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1.5}>
        <Box>
          <Stack direction="row" spacing={1} useFlexGap alignItems="center" sx={{ mb: 1, flexWrap: 'wrap' }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{session.title}</Typography>
          </Stack>
          <Stack spacing={0.5}>
            <Stack direction="row" spacing={1} alignItems="center">
              <CalendarMonthIcon fontSize="small" />
              <Typography variant="body2">{formatDate(session.date)}</Typography>
            </Stack>
            <Stack direction="row" spacing={1} alignItems="center">
              <AccessTimeIcon fontSize="small" />
              <Typography variant="body2">{formatTime(session.time)}</Typography>
            </Stack>
            <Stack direction="row" spacing={1} alignItems="center">
              <PlaceIcon fontSize="small" />
              <Typography variant="body2">{session.location}</Typography>
            </Stack>
          </Stack>
        </Box>
        {sessionAvailabilityLabel && <Chip label={sessionAvailabilityLabel} color={isAvailable ? 'default' : 'error'} />}
      </Stack>

      {!isSelected && sessionTicketTypes.length > 0 && (
        <Stack direction="row" spacing={1} useFlexGap sx={{ mt: 2, flexWrap: 'wrap' }}>
          {sessionTicketTypes.map((ticket) => (
            <Chip
              key={`${session.id}-${ticket.code}`}
              label={`${ticket.label} - ${formatCurrency(ticket.price)}${shouldShowRemainingTickets && ticket.session_inventory_configured ? ` (${ticket.session_seats_left ?? 0} left)` : ''}`}
              sx={registrationStyles.ticketPreviewChip(isTicketAvailable(ticket))}
            />
          ))}
        </Stack>
      )}

      {isSelected && isAvailable && (
        <Box sx={registrationStyles.selectedArea} onClick={(event) => event.stopPropagation()}>
          <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1 }}>Choose ticket</Typography>
          {selectableTicketCount === 0 && <Alert severity="warning" sx={registrationStyles.warning}>No ticket types are currently available for this session.</Alert>}

          <Grid container spacing={1.5} sx={{ alignItems: 'stretch' }}>
            {sessionTicketTypes.map((ticket) => {
              const ticketAvailable = isTicketAvailable(ticket);
              return (
                <TicketCard
                  key={`${session.id}-${ticket.code}-choice`}
                  ticket={ticket}
                  selected={selectedTicketCode === ticket.code}
                  available={ticketAvailable}
                  seatsLabel={shouldShowRemainingTickets && ticket.session_inventory_configured ? `${ticket.session_seats_left ?? 0} left` : ''}
                  quantity={quantity}
                  quantityLimit={getTicketQuantityLimit(ticket)}
                  formErrors={formErrors}
                  onSelect={onTicketSelect}
                  onQuantityChange={onQuantityChange}
                />
              );
            })}
          </Grid>

          <RegistrationSummary
            session={session}
            selectedTicket={selectedTicket}
            selectedTicketSeatsLeft={selectedTicketSeatsLeft}
            quantity={quantity}
            totalPrice={totalPrice}
            disabled={summaryDisabled}
            onConfirm={(event) => {
              event.stopPropagation();
              onOpenRegistration();
            }}
          />
        </Box>
      )}
    </Box>
  );
};

export default SessionCard;
